import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { getSlugRedirect, getContent } from "@/lib/cms.functions";
import { SITE_URL, LOCALE_HREFLANG, localePath, normalizeLocale } from "@/lib/i18n";
import { normalizeMediaUrl } from "@/lib/media";
import { ArticleView } from "@/components/site/ContentViews";
import { ClosingCta } from "@/components/site/Cta";
import { NotFoundBlock } from "@/components/site/NotFoundBlock";
import { translate } from "@/lib/i18n-dict";
import { faqScriptFromEntries, parseFaqEntriesFromMarkdown } from "@/lib/faq-schema";
import { blogIllustrationUrl } from "@/content/blog-illustrations";

// Permanent redirects for legacy (Webflow) blog URLs.
const LEGACY_BLOG_REDIRECTS: Record<string, string> = {
  "mps-mrp-rccp": "/blog/master-production-schedule",
  "monthly-production-planning": "/blog/master-production-schedule",
  "understanding-the-difference-between-mrp-and-mrpii": "/blog/mrp-vs-mrp-ii",
  "items-how-to-manage-them-with-ease": "/blog/item-master-data-variants",
  "the-hidden-problem-in-automotive-manufacturing----its-planning": "/blog/automotive-production-planning",
  "planning-vs-scheduling-in-manufacturing-why-the-difference-mattersmore-than-you-think":
    "/blog/production-planning-vs-scheduling",
  "planning-vs-scheduling-in-manufacturing-why-the-difference-matters-more-than-you-think":
    "/blog/production-planning-vs-scheduling",
  "how-the-cable-industry-loses-profit-at-the-planning-stage----and-why-it-becomes-a-systemic-business-problem":
    "/blog/cable-manufacturing-planning",
  "sap-manufacturing-integration": "/integrations/sap",
  "oracle-manufacturing-integration": "/blog/production-orders-integration",
  "bill-of-materials-routing": "/blog/cable-tds-norms-technology",
};

export const Route = createFileRoute("/{-$locale}/blog/$slug")({
  staticData: { sitemap: true },
  loader: async ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const legacy = LEGACY_BLOG_REDIRECTS[params.slug.toLowerCase()];
    if (legacy) throw redirect({ href: localePath(locale, legacy), statusCode: 301 });
    const item = await getContent({ data: { kind: "blog", slug: params.slug, locale } });
    if (!item) {
      const current = await getSlugRedirect({ data: { kind: "blog", slug: params.slug, locale } });
      if (current) throw redirect({ href: localePath(locale, `/blog/${encodeURIComponent(current)}`), statusCode: 301 });
      throw notFound();
    }
    if (item.localizedSlug && item.localizedSlug !== params.slug) {
      throw redirect({ to: "/{-$locale}/blog/$slug", params: { locale, slug: item.localizedSlug }, statusCode: 301 });
    }
    return { locale, item };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Article unavailable | factory.online" }, { name: "robots", content: "noindex" }],
      };
    }
    const { item, locale } = loaderData;
    const articlePath = localePath(locale, `/blog/${item.localizedSlug || item.slug}`);
    const articleUrl = `${SITE_URL}${articlePath}`;
    const cover = normalizeMediaUrl(blogIllustrationUrl(item.slug, item.cover_url));
    const absoluteCover = cover?.startsWith("https://")
      ? cover
      : cover?.startsWith("/")
        ? `${SITE_URL}${cover}`
        : undefined;
    const articleFaqs = parseFaqEntriesFromMarkdown(item.body);
    const alternates = item.localePaths.map((entry) => ({
      rel: "alternate",
      hrefLang: LOCALE_HREFLANG[normalizeLocale(entry.locale)],
      href: `${SITE_URL}${localePath(normalizeLocale(entry.locale), `/blog/${entry.slug}`)}`,
    }));
    const english = item.localePaths.find((entry) => entry.locale === "en");
    return {
      meta: [
        { title: `${item.seoTitle} | factory.online` },
        { name: "description", content: item.seoDescription },
        { property: "og:title", content: item.seoTitle },
        { property: "og:description", content: item.seoDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: articleUrl },
        { property: "og:locale", content: locale },
        { name: "twitter:card", content: "summary_large_image" },
        ...(absoluteCover
          ? [
              { property: "og:image", content: absoluteCover },
              { name: "twitter:image", content: absoluteCover },
            ]
          : []),
      ],
      links: [
        ...alternates,
        ...(english
          ? [
              {
                rel: "alternate",
                hrefLang: "x-default",
                href: `${SITE_URL}${localePath("en", `/blog/${english.slug}`)}`,
              },
            ]
          : []),
        { rel: "canonical", href: articleUrl },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: item.title,
            description: item.seoDescription || item.excerpt,
            ...(item.published_at
              ? { datePublished: item.published_at, dateModified: item.updated_at }
              : item.updated_at
                ? { dateModified: item.updated_at }
                : {}),
            ...(absoluteCover ? { image: [absoluteCover] } : {}),
            author: { "@type": "Organization", name: "factory.online", url: SITE_URL },
            publisher: {
              "@type": "Organization",
              name: "factory.online",
              url: SITE_URL,
            },
            mainEntityOfPage: articleUrl,
            inLanguage: item.locale,
          }),
        },
        ...faqScriptFromEntries(articleFaqs),
      ],
    };
  },
  notFoundComponent: BlogNotFound,
  component: BlogArticle,
});

function BlogNotFound() {
  return (
    <NotFoundBlock
      title="Article not found"
      lead="This article may have been moved or unpublished."
      backTo="/blog"
      backLabel="Back to the blog"
    />
  );
}

function BlogArticle() {
  const { item, locale } = Route.useLoaderData();
  return (
    <>
      <ArticleView item={item} backTo="/blog" backLabel={translate(locale, "All articles")} locale={locale} />
      <ClosingCta />
    </>
  );
}
